import { useQuery } from '@tanstack/react-query';
import {
  CreditCard,
  TrendingUp,
  Users,
  HardDrive,
  FileText,
  Download,
  CheckCircle,
} from 'lucide-react';
import apiClient from '@/api/client';

interface BillingPlan {
  name: string;
  status: string;
  price: number;
  currency: string;
  billing_cycle: string;
  features: string[];
  current_period_start: string;
  current_period_end: string;
}

interface UsageMetrics {
  users: { current: number; limit: number };
  exams: { current: number; limit: number };
  storage: { current_bytes: number; limit_bytes: number };
  api_calls: { current: number; limit: number };
}

interface Invoice {
  id: string;
  date: string;
  amount: number;
  currency: string;
  status: string;
  description: string;
  download_url: string;
}

interface BillingCurrentResponse {
  plan: BillingPlan;
  usage: UsageMetrics;
}

interface BillingHistoryResponse {
  invoices: Invoice[];
}

const defaultPlan: BillingPlan = {
  name: 'Enterprise',
  status: 'active',
  price: 0,
  currency: 'USD',
  billing_cycle: 'monthly',
  features: [
    'Unlimited exams',
    'AI-powered proctoring',
    'Advanced analytics',
    'Multi-tenant support',
    'Priority support',
    'Custom branding',
    'SSO integration',
    'API access',
  ],
  current_period_start: new Date().toISOString(),
  current_period_end: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
};

const defaultUsage: UsageMetrics = {
  users: { current: 0, limit: 0 },
  exams: { current: 0, limit: 0 },
  storage: { current_bytes: 0, limit_bytes: 0 },
  api_calls: { current: 0, limit: 0 },
};

const formatBytes = (bytes: number) => {
  if (bytes === 0) return '0 B';
  if (bytes >= 1e9) return `${(bytes / 1e9).toFixed(1)} GB`;
  if (bytes >= 1e6) return `${(bytes / 1e6).toFixed(1)} MB`;
  if (bytes >= 1e3) return `${(bytes / 1e3).toFixed(1)} KB`;
  return `${bytes} B`;
};

const formatNumber = (num: number) => {
  if (num >= 1e6) return `${(num / 1e6).toFixed(1)}M`;
  if (num >= 1e3) return `${(num / 1e3).toFixed(1)}K`;
  return num.toLocaleString();
};

const formatDate = (dateStr: string) => {
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return dateStr;
  }
};

const formatCurrency = (amount: number, currency: string) => {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
    }).format(amount);
  } catch {
    return `$${amount.toFixed(2)}`;
  }
};

const getStatusBadge = (status: string) => {
  switch (status.toLowerCase()) {
    case 'active':
    case 'paid':
      return 'bg-emerald-500/20 text-emerald-400';
    case 'past_due':
    case 'overdue':
      return 'bg-red-500/20 text-red-400';
    case 'pending':
      return 'bg-amber-500/20 text-amber-400';
    case 'trialing':
      return 'bg-cyan-500/20 text-cyan-400';
    default:
      return 'bg-white/10 text-white/60';
  }
};

export const BillingPage = () => {
  const { data: currentData, isLoading: currentLoading } = useQuery({
    queryKey: ['billing-current'],
    queryFn: async () => {
      try {
        const res = await apiClient.get<{ data: BillingCurrentResponse }>('/billing/current');
        return res.data?.data || null;
      } catch (err: any) {
        if (err?.response?.status === 404) return null;
        throw err;
      }
    },
    retry: false,
  });

  const { data: historyData, isLoading: historyLoading } = useQuery({
    queryKey: ['billing-history'],
    queryFn: async () => {
      try {
        const res = await apiClient.get<{ data: BillingHistoryResponse }>('/billing/history');
        return res.data?.data || null;
      } catch (err: any) {
        if (err?.response?.status === 404) return null;
        throw err;
      }
    },
    retry: false,
  });

  const plan = currentData?.plan || defaultPlan;
  const usage = currentData?.usage || defaultUsage;
  const invoices: Invoice[] = historyData?.invoices || [];

  const usagePercent = (current: number, limit: number) => {
    if (limit <= 0) return 0;
    return Math.min(100, (current / limit) * 100);
  };

  const getUsageColor = (percent: number) => {
    if (percent >= 90) return 'bg-red-500';
    if (percent >= 70) return 'bg-amber-500';
    return 'bg-teal-500';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white/90">Billing & Subscription</h1>
        <p className="text-sm text-white/50 mt-1">
          Manage your plan, usage, and billing history
        </p>
      </div>

      {/* Current Plan */}
      <div className="card-dark p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-lg bg-teal-500/20 flex items-center justify-center">
              <CreditCard className="w-5 h-5 text-teal-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white/90">Current Plan</h2>
              <p className="text-xs text-white/40">
                {plan.current_period_start && plan.current_period_end
                  ? `${formatDate(plan.current_period_start)} - ${formatDate(plan.current_period_end)}`
                  : 'Current billing period'}
              </p>
            </div>
          </div>
          <span
            className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${getStatusBadge(plan.status)}`}
          >
            {plan.status}
          </span>
        </div>

        <div className="flex items-baseline space-x-2 mb-4">
          <h3 className="text-2xl font-bold text-white/90">{plan.name}</h3>
          {plan.price > 0 && (
            <span className="text-white/50 text-sm">
              {formatCurrency(plan.price, plan.currency)} / {plan.billing_cycle}
            </span>
          )}
        </div>

        {plan.features.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {plan.features.map((feature, idx) => (
              <div key={idx} className="flex items-center space-x-2 text-sm">
                <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                <span className="text-white/70">{feature}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Usage Metrics */}
      <div>
        <h2 className="text-lg font-semibold text-white/90 mb-3">Usage</h2>
        {currentLoading ? (
          <div className="card-dark p-8 text-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-400 mx-auto mb-2" />
            <p className="text-sm text-white/50">Loading usage data...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Users */}
            <div className="card-dark p-4">
              <div className="flex items-center space-x-3 mb-3">
                <div className="w-9 h-9 rounded-lg bg-cyan-500/20 flex items-center justify-center">
                  <Users className="w-4.5 h-4.5 text-cyan-400" />
                </div>
                <div>
                  <p className="text-xs text-white/50">Users</p>
                  <p className="text-lg font-semibold text-white/90">
                    {formatNumber(usage.users.current)}
                    {usage.users.limit > 0 && (
                      <span className="text-xs text-white/40 font-normal ml-1">
                        / {formatNumber(usage.users.limit)}
                      </span>
                    )}
                  </p>
                </div>
              </div>
              {usage.users.limit > 0 && (
                <div className="w-full h-1.5 bg-white/[0.06] rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${getUsageColor(usagePercent(usage.users.current, usage.users.limit))}`}
                    style={{
                      width: `${usagePercent(usage.users.current, usage.users.limit)}%`,
                    }}
                  />
                </div>
              )}
            </div>

            {/* Exams */}
            <div className="card-dark p-4">
              <div className="flex items-center space-x-3 mb-3">
                <div className="w-9 h-9 rounded-lg bg-purple-500/20 flex items-center justify-center">
                  <FileText className="w-4.5 h-4.5 text-purple-400" />
                </div>
                <div>
                  <p className="text-xs text-white/50">Exams</p>
                  <p className="text-lg font-semibold text-white/90">
                    {formatNumber(usage.exams.current)}
                    {usage.exams.limit > 0 && (
                      <span className="text-xs text-white/40 font-normal ml-1">
                        / {formatNumber(usage.exams.limit)}
                      </span>
                    )}
                  </p>
                </div>
              </div>
              {usage.exams.limit > 0 && (
                <div className="w-full h-1.5 bg-white/[0.06] rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${getUsageColor(usagePercent(usage.exams.current, usage.exams.limit))}`}
                    style={{
                      width: `${usagePercent(usage.exams.current, usage.exams.limit)}%`,
                    }}
                  />
                </div>
              )}
            </div>

            {/* Storage */}
            <div className="card-dark p-4">
              <div className="flex items-center space-x-3 mb-3">
                <div className="w-9 h-9 rounded-lg bg-amber-500/20 flex items-center justify-center">
                  <HardDrive className="w-4.5 h-4.5 text-amber-400" />
                </div>
                <div>
                  <p className="text-xs text-white/50">Storage</p>
                  <p className="text-lg font-semibold text-white/90">
                    {formatBytes(usage.storage.current_bytes)}
                    {usage.storage.limit_bytes > 0 && (
                      <span className="text-xs text-white/40 font-normal ml-1">
                        / {formatBytes(usage.storage.limit_bytes)}
                      </span>
                    )}
                  </p>
                </div>
              </div>
              {usage.storage.limit_bytes > 0 && (
                <div className="w-full h-1.5 bg-white/[0.06] rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${getUsageColor(usagePercent(usage.storage.current_bytes, usage.storage.limit_bytes))}`}
                    style={{
                      width: `${usagePercent(usage.storage.current_bytes, usage.storage.limit_bytes)}%`,
                    }}
                  />
                </div>
              )}
            </div>

            {/* API Calls */}
            <div className="card-dark p-4">
              <div className="flex items-center space-x-3 mb-3">
                <div className="w-9 h-9 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                  <TrendingUp className="w-4.5 h-4.5 text-emerald-400" />
                </div>
                <div>
                  <p className="text-xs text-white/50">API Calls</p>
                  <p className="text-lg font-semibold text-white/90">
                    {formatNumber(usage.api_calls.current)}
                    {usage.api_calls.limit > 0 && (
                      <span className="text-xs text-white/40 font-normal ml-1">
                        / {formatNumber(usage.api_calls.limit)}
                      </span>
                    )}
                  </p>
                </div>
              </div>
              {usage.api_calls.limit > 0 && (
                <div className="w-full h-1.5 bg-white/[0.06] rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${getUsageColor(usagePercent(usage.api_calls.current, usage.api_calls.limit))}`}
                    style={{
                      width: `${usagePercent(usage.api_calls.current, usage.api_calls.limit)}%`,
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Billing History */}
      <div>
        <h2 className="text-lg font-semibold text-white/90 mb-3">Billing History</h2>
        <div className="card-dark overflow-hidden">
          {historyLoading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-400 mx-auto mb-2" />
              <p className="text-sm text-white/50">Loading billing history...</p>
            </div>
          ) : invoices.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="w-10 h-10 text-white/20 mx-auto mb-3" />
              <p className="text-white/60">No billing history available</p>
              <p className="text-xs text-white/40 mt-1">
                Invoices will appear here once generated
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="table-dark w-full text-sm">
                <thead>
                  <tr className="border-b border-white/[0.06]">
                    <th className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-white/50 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/[0.04]">
                  {invoices.map((invoice) => (
                    <tr
                      key={invoice.id}
                      className="hover:bg-white/[0.02] transition-colors"
                    >
                      <td className="px-4 py-3 whitespace-nowrap text-white/70 text-xs">
                        {formatDate(invoice.date)}
                      </td>
                      <td className="px-4 py-3 text-white/80 text-xs">
                        {invoice.description || 'Subscription charge'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-white/90 text-xs font-medium">
                        {formatCurrency(invoice.amount, invoice.currency)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span
                          className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${getStatusBadge(invoice.status)}`}
                        >
                          {invoice.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right">
                        {invoice.download_url && (
                          <a
                            href={invoice.download_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center space-x-1 text-teal-400 hover:text-teal-300 text-xs"
                          >
                            <Download className="w-3.5 h-3.5" />
                            <span>Download</span>
                          </a>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
